import React, { useState, useEffect } from 'react';
import { TextField, Button, Box, Typography } from '@mui/material';
import {
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ColorPicker, { LIGHT_COLOR_LIST, DARK_COLOR_LIST } from './ColorPicker';
import { getDefaultDateTime } from '../common/AppUtils';

export default function AddModal({
  open,
  columns,
  relatedData,
  rows,
  handleClose,
  handleSave,
  add_title="Add Data",
  color_shade="light"
}) {
  const [formData, setFormData] = useState({});
  const [addErrors, setAddErrors] = useState({});

  useEffect(() => {
    const initialData = {};
    columns.forEach((field) => {
      if (field.type === 'choice' && relatedData[field.attr]) {
        if (field.default) {
          initialData[field.attr] = field.default;
        } else {
          const firstOptionKey = Object.keys(relatedData[field.attr])[0];
          if (firstOptionKey) {
            initialData[field.attr] = firstOptionKey;
          }
        }
      }
      else if (field.type === 'datetime') {
        initialData[field.attr] = getDefaultDateTime(field.attr);
      }
      else if (field.attr === 'color') {
        const COLOR_CHOICE_LIST = color_shade === "light" ? LIGHT_COLOR_LIST : DARK_COLOR_LIST;
        initialData[field.attr] = COLOR_CHOICE_LIST[rows.length % COLOR_CHOICE_LIST.length];
      }
    });
    setFormData(initialData);
    setAddErrors({});
  }, [columns, relatedData, rows, open]);

  const handleChange = (fieldAttr, value) => {
    setFormData({
      ...formData,
      [fieldAttr]: value,
    });

    // Clear error when user edits the field
    setAddErrors((prevErrors) => ({
      ...prevErrors,
      [fieldAttr]: undefined,
    }));
  };

  const handleColorChange = (color) => {
    handleChange('color', color);
  };

  const handleSuccess = () => {
    // Clear or update values in the form as needed
    setFormData({});
    setAddErrors({});
  };

  const handleAddFailure = (errorDict) => {
    console.log(errorDict);
    // Highlight errors
    setAddErrors(errorDict); // Set errors from backend
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 24
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
        <Typography variant="h3">{add_title}</Typography>
        {true && ( // ToDo: !isSubmitting
          <IconButton edge="end" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <Divider />
      
      <DialogContent>
        <Box
          component="form"
          noValidate
          sx={{
            display: 'flex',
            flexDirection: 'column',
            mb: 1,
            gap: 2,
          }}
        >
          {columns.map((field) =>
            !field.extra && ( // Only render if field.extra is false
              <Box key={field.attr}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Typography variant="h5" gutterBottom>
                    {field.label}
                    {field.required && (
                      <Typography component="span" color="error" ml={0.5}>
                        *
                      </Typography>
                    )}
                  </Typography>
                  <Tooltip title={field.help_text} arrow>
                    <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'pointer', mt: '-2px' }} />
                  </Tooltip>
                </Box>
                {field.type === 'choice' && relatedData[field.attr] ? (
                  <FormControl
                    fullWidth
                    error={Boolean(addErrors[field.attr])}
                  >
                    {/* <InputLabel id={field.attr}>{field.label}</InputLabel> */}
                    <Select
                      // labelId={field.attr}
                      id={field.attr}
                      value={formData[field.attr]}
                      // label={field.label}
                      onChange={(e) => handleChange(field.attr, e.target.value)}
                      fullWidth
                      displayEmpty // This allows showing placeholder when value is empty
                    >
                      {/* Add placeholder/clear option for nullable fields */}
                      {!field.required && (
                        <MenuItem value="">
                          <em style={{ color: '#666' }}>Select {field.label}</em>
                        </MenuItem>
                      )}
                      
                      {Object.entries(relatedData[field.attr]).map(
                        ([option_key, option_value]) => (
                          <MenuItem key={option_key} value={option_key}>
                            {option_value.label}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                    {addErrors[field.attr] && (
                      <FormHelperText>
                        {addErrors[field.attr]?.[0]}
                      </FormHelperText>
                    )}
                  </FormControl>
                ) : field.attr === 'color' ? (
                  <ColorPicker handleColorChange={handleColorChange} color_shade={color_shade} defaultColor={formData[field.attr]}/>
                ) : field.type === 'datetime' ? (
                  <TextField
                    fullWidth
                    // key={field.attr} // key defined on outer box
                    // label={field.label}
                    name={field.attr}
                    type="datetime-local"
                    value={formData[field.attr] || getDefaultDateTime(field.attr)}
                    onChange={(e) => handleChange(field.attr, e.target.value)}
                    required={field.required || false}
                    disabled={field.editable === false}
                    error={Boolean(addErrors[field.attr])}
                    helperText={addErrors[field.attr]?.[0] || ''}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      step: 300, // 300 seconds = 5 minutes
                    }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    key={field.attr}
                    // label={field.label}
                    name={field.attr}
                    type={field.sub_type || 'text'}
                    value={formData[field.attr] || ''}
                    onChange={(e) => handleChange(field.attr, e.target.value)}
                    required={field.required || false}
                    error={Boolean(addErrors[field.attr])}
                    helperText={addErrors[field.attr]?.[0] || ''}
                  />
                )}
              </Box>
            ),
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={() => handleSave(formData, handleSuccess, handleAddFailure)}
          variant="contained"
          color="primary"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
